import { useMemo } from 'react'
import { Autocomplete, AutocompleteProps, CircularProgress, TextField } from '@mui/material'

export type AsyncAutocompleteOption = {
  label: string
  value: string | number
}

export type AsyncDataResponse<TData = unknown> = {
  data: TData | undefined
  isLoading: boolean
  error?: Error | null
}

export type AsyncAutocompleteProps<TData = unknown> = Omit<
  AutocompleteProps<AsyncAutocompleteOption, boolean, false, false>,
  'options' | 'loading' | 'renderInput' | 'onChange' | 'value'
> & {
  swrResponse: AsyncDataResponse<TData>
  mapDataToOptions: (_data: TData | undefined) => AsyncAutocompleteOption[]
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  value?: (string | number)[]
  onChange?: (_values: (string | number)[]) => void
}

const AsyncAutocomplete = ({
  swrResponse,
  mapDataToOptions,
  label,
  placeholder,
  error,
  helperText,
  value = [],
  onChange,
  multiple = true,
  disabled = false,
  ...autocompleteProps
}: AsyncAutocompleteProps) => {
  const { data, isLoading } = swrResponse

  const {
    sx: autocompleteSx,
    fullWidth: autocompleteFullWidth = true,
    ...restAutocompleteProps
  } = autocompleteProps

  const options = useMemo(() => mapDataToOptions(data), [data, mapDataToOptions])

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  )

  const selectedValue = multiple ? selectedOptions : selectedOptions[0] || null

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: AsyncAutocompleteOption | AsyncAutocompleteOption[] | null
  ) => {
    if (!onChange) return

    if (multiple) {
      const selectedValues = Array.isArray(newValue)
        ? newValue.map((option) => option.value)
        : newValue
          ? [newValue.value]
          : []
      onChange(selectedValues)
    } else {
      const selectedValue = newValue && !Array.isArray(newValue) ? newValue.value : null
      onChange(selectedValue ? [selectedValue] : [])
    }
  }

  const isDisabled = disabled

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      fullWidth={autocompleteFullWidth}
      value={selectedValue}
      onChange={handleChange}
      loading={isLoading}
      disabled={isDisabled}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      sx={{
        '& .MuiAutocomplete-endAdornment': {
          right: '3px !important'
        },
        '& .MuiAutocomplete-clearIndicator': {
          position: 'relative',
          right: '3px !important'
        },
        '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
          width: '28px',
          height: '28px',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        ...autocompleteSx
      }}
      slotProps={{
        chip: {
          variant: 'outlined',
          size: 'small'
        },
        popupIndicator: {
          disableRipple: true,
          disableFocusRipple: true,
          disableTouchRipple: true
        },
        clearIndicator: {
          disableRipple: true,
          disableFocusRipple: true,
          disableTouchRipple: true
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={autocompleteFullWidth}
          label={label}
          placeholder={selectedOptions.length === 0 ? placeholder : undefined}
          error={error}
          helperText={helperText}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      {...restAutocompleteProps}
    />
  )
}

export default AsyncAutocomplete
